/**
 * The XR application: renderer, player rig, input, locomotion, scenes and the frame loop.
 */
import * as THREE from 'three';
import type { Manifest, RoomSummary } from '@clinspace/shared';
import { AudioEngine } from '../audio/AudioEngine.ts';
import { api, getJson } from '../lib/api.ts';
import { LobbyScene } from '../scenes/LobbyScene.ts';
import { RoomScene } from '../scenes/RoomScene.ts';
import type { AppContext, SceneController } from '../scenes/Scene.ts';
import { InteractiveSystem } from '../ui/Interactive.ts';
import { updateUi } from '../ui/Panel.ts';
import { AvatarLoader } from '../world/avatars.ts';
import { InputManager } from '../xr/InputManager.ts';
import { Locomotion } from '../xr/Locomotion.ts';
import { PlayerRig } from '../xr/PlayerRig.ts';
import { Vignette } from '../xr/Vignette.ts';
import { Net } from './net.ts';
import { VoiceMesh } from '../audio/VoiceMesh.ts';
import { RemoteAvatarManager } from '../avatars/RemoteAvatarManager.ts';
import { LocalPose } from '../xr/LocalPose.ts';
import { WristMenu } from '../ui/WristMenu.ts';
import { DebugHud } from '../ui/DebugHud.ts';
import { POSE_RATE_HZ } from '@clinspace/shared';
import { loadProfile, saveProfile, type Profile } from './profile.ts';
import { loadFonts } from '../ui/theme.ts';
import { GestureDetector, type Gesture, type GestureHand } from '../xr/gestures.ts';
import { REACTION_EMOJI } from '../avatars/RemoteAvatarManager.ts';
import { sendJson } from '../lib/api.ts';
import { micMayPrompt } from '../audio/micPermission.ts';
import { isVisionOs } from './device.ts';
import type { Reaction } from '@clinspace/shared';

type XrNavigator = Navigator & {
  xr?: {
    isSessionSupported(mode: string): Promise<boolean>;
    requestSession(mode: string, init?: unknown): Promise<XRSession>;
  };
};

export interface AppHooks {
  onSessionEnd?: () => void;
  onStats?: (stats: {
    fps: number;
    frameMs: number;
    triangles: number;
    drawCalls: number;
    scene: string;
  }) => void;
}

export class App {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly rig: PlayerRig;
  readonly input: InputManager;
  readonly locomotion: Locomotion;
  readonly interactive: InteractiveSystem;
  readonly vignette: Vignette;
  readonly audio = new AudioEngine();
  readonly net = new Net();
  readonly avatars: AvatarLoader;
  readonly voice: VoiceMesh;
  readonly remotes: RemoteAvatarManager;
  readonly localPose: LocalPose;
  readonly wristMenu: WristMenu;
  readonly debugHud: DebugHud;
  private poseClock = 0;
  private menuButtonWasDown = false;
  private lastStats = { fps: 0, frameMs: 0, triangles: 0, drawCalls: 0 };
  private readonly remoteShares = new Map<string, MediaStream>();
  handRaised = false;
  readonly gestures = new GestureDetector();
  /** True while the WebSocket is down and reconnecting. */
  connectionLost = false;
  /** Dev hooks. */
  lastGesture: Gesture | undefined;
  lastScreenshot: { file: string; study: string } | undefined;
  readonly profile: Profile;
  manifest: Manifest | undefined;
  rooms: RoomSummary[] = [];
  private current: SceneController | undefined;
  private pendingSwitch: (() => SceneController) | undefined;
  private switching = false;
  private readonly timer = new THREE.Timer();
  private frames = 0;
  private accumulator = 0;
  private lastLog = performance.now();
  private started = false;

  constructor(
    readonly canvas: HTMLCanvasElement,
    private readonly hooks: AppHooks = {},
  ) {
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.renderer.xr.enabled = true;
    this.scene.background = new THREE.Color('#dfe7ee');
    this.scene.fog = new THREE.Fog('#dfe7ee', 40, 90);

    this.rig = new PlayerRig(window.innerWidth / window.innerHeight);
    this.scene.add(this.rig.group);
    this.vignette = new Vignette(this.rig.camera);
    this.input = new InputManager(this.renderer, this.rig, canvas);
    this.profile = loadProfile();
    this.locomotion = new Locomotion(this.rig, this.input, this.vignette, this.scene, {
      smoothLocomotion: this.profile.smoothLocomotion,
      snapAngleDeg: this.profile.snapAngleDeg,
      onTeleport: () => {
        this.audio.whoosh();
        this.localPose.teleported = true;
      },
      onRecalibrate: (offset) =>
        console.log(`[clinspace] height recalibrated, offset ${offset.toFixed(2)} m`),
      onSeated: (seated) =>
        console.log(
          `[clinspace] seated mode ${seated ? 'on: view raised to standing height' : 'off'}`,
        ),
    });
    this.interactive = new InteractiveSystem(this.input, this.locomotion);
    this.avatars = new AvatarLoader(this.renderer);
    this.localPose = new LocalPose(this.rig, this.input);
    this.remotes = new RemoteAvatarManager(this.avatars, this.rig.camera);
    this.scene.add(this.remotes.group);
    this.debugHud = new DebugHud(this.rig.camera);
    this.remotes.voiceLevel = (id) => this.voice.level(id);
    this.voice = new VoiceMesh({
      sendSignal: (to, data) => this.net.sendSignal(to, data),
      onPeerState: (id, state) => console.log(`[voice] peer ${id.slice(-6)} ${state}`),
      onVideo: (id, stream) => {
        if (stream) this.remoteShares.set(id, stream);
        else this.remoteShares.delete(id);
        this.roomDebug()?.dashboards?.share.onRemote(id, stream);
      },
    });
    this.wristMenu = new WristMenu(this.interactive, {
      teleport: (position) => this.locomotion.teleport(position),
      toggleMute: () => this.toggleMute(),
      recalibrate: () => this.locomotion.recalibrate(),
      setSnapAngle: (deg) => {
        this.locomotion.options.snapAngleDeg = deg;
        this.profile.snapAngleDeg = deg;
        saveProfile(this.profile);
      },
      toggleSmoothLocomotion: () => {
        this.locomotion.options.smoothLocomotion = !this.locomotion.options.smoothLocomotion;
        this.profile.smoothLocomotion = this.locomotion.options.smoothLocomotion;
        saveProfile(this.profile);
        return this.locomotion.options.smoothLocomotion;
      },
      toggleDebugHud: () => {
        this.debugHud.setVisible(!this.debugHud.visible);
        return this.debugHud.visible;
      },
      leaveRoom: () => {
        this.wristMenu.setOpen(false);
        if (this.current?.canLeave) void this.switchTo(() => new LobbyScene(this.context()));
      },
      toggleAnnotate: () => this.roomDebug()?.dashboards?.toggleAnnotate() ?? false,
      togglePoint: () => this.roomDebug()?.dashboards?.togglePoint() ?? false,
      cycleFollow: () => this.roomDebug()?.dashboards?.cycleFollow(),
      tidyRoom: () => this.roomDebug()?.dashboards?.tidyRoom(),
      annotating: () => this.roomDebug()?.dashboards?.annotating ?? false,
      pointing: () => this.roomDebug()?.dashboards?.pointing ?? false,
      following: () => this.roomDebug()?.dashboards?.followingName(),
      togglePresent: () => this.roomDebug()?.dashboards?.presentation.toggle(),
      toggleShare: () => {
        const share = this.roomDebug()?.dashboards?.share;
        if (!share) return;
        if (share.sharing) share.stop();
        else void share.start();
      },
      pullScreen: () => this.roomDebug()?.dashboards?.pullScreen(),
      toggleHand: () => this.toggleHand(),
      presenting: () => this.roomDebug()?.dashboards?.presentation.presenting ?? false,
      sharing: () => this.voice.sharing,
      handRaised: () => this.handRaised,
      canShare: () => !this.renderer.xr.isPresenting && !!this.roomDebug()?.dashboards,
      react: (reaction) => this.react(reaction),
      isMuted: () => this.voice.isMuted,
      snapAngle: () => this.locomotion.options.snapAngleDeg,
      smoothLocomotion: () => this.locomotion.options.smoothLocomotion,
      debugHud: () => this.debugHud.visible,
    });
    this.scene.add(this.wristMenu.group, this.wristMenu.orb);
    this.input.onKey((e) => {
      if (this.input.captureText) return;
      if (e.code === 'Tab' && this.started) {
        e.preventDefault();
        this.wristMenu.toggle();
        // Desktop: the menu frees the mouse so its buttons can be clicked; closing it
        // captures the mouse again for looking around.
        if (!this.renderer.xr.isPresenting && this.input.lockOnClick) {
          if (this.wristMenu.open) {
            if (document.pointerLockElement) document.exitPointerLock();
          } else this.input.requestLock();
        }
      }
      if (e.code === 'KeyM' && this.started) this.wristMenu.refresh(this.toggleMute());
      const room = this.roomDebug()?.dashboards;
      if (room && this.started) {
        if (e.code === 'KeyN') room.toggleAnnotate();
        else if (e.code === 'KeyP') room.togglePoint();
        else if (e.code === 'KeyF') room.cycleFollow();
        else if (e.code === 'KeyT') room.tidyRoom();
        else if (e.code === 'KeyC') room.reception.engage();
        else if (e.code === 'KeyI') room.presentation.toggle();
        else if (e.code === 'KeyU') {
          if (room.share.sharing) room.share.stop();
          else void room.share.start();
        } else if (e.code === 'KeyO') room.pullScreen();
        else if (e.code === 'BracketRight') room.presentation.step(1);
        else if (e.code === 'BracketLeft') room.presentation.step(-1);
        else if (e.code === 'KeyK') room.presentation.setSpotlight(!room.presentation.spotlightOn);
        else if (e.code === 'KeyJ') room.presentation.acceptRecentre();
        else if (e.code === 'KeyY') this.toggleHand();
        else if (e.code === 'KeyB') this.react('thumbsUp');
        else if (e.code === 'KeyV') this.react('clap');
        else if (e.code === 'KeyX') this.react('wave');
        // Shift + / is ? (the controls list, main.ts); / alone is the question reaction.
        else if (e.code === 'Slash' && !e.shiftKey) this.react('question');
        else if (e.code === 'KeyZ') void this.takeScreenshot();
        else return;
        this.wristMenu.refreshCollab();
      }
    });

    // Presence: remote avatars and voice peers follow room membership.
    this.net.onSnapshot((members, poses) => {
      this.remotes.setMembers(members, this.net.sessionId, poses);
      this.voice.removeAll();
      for (const m of this.net.others()) this.voice.addPeer(m.sessionId, true);
    });
    this.net.onMemberJoined((member) => this.remotes.upsert(member));
    this.net.onMemberLeft((id) => {
      this.remotes.remove(id);
      this.voice.removePeer(id);
    });
    this.net.onMemberUpdate((id, patch) => this.remotes.updateMember(id, patch));
    this.net.onReaction((from, reaction) => {
      this.remotes.showReaction(from.sessionId, reaction);
      this.roomDebug()?.dashboards?.showNotice([`${from.name} ${REACTION_EMOJI[reaction]}`], 1800);
    });
    this.net.onPose((id, pose) => this.remotes.onPose(id, pose));
    this.net.onSignal((from, data) => this.voice.signal(from, data));
    this.net.onError((code) => {
      if (code === 'not_identified' || code === 'bad_message') {
        this.profile.onboarded = false;
        saveProfile(this.profile);
        if (this.current && this.current.name !== 'lobby') {
          void this.switchTo(() => new LobbyScene(this.context()));
        }
      }
    });

    // Capturing the mouse again (a click on empty space) closes the desktop wrist menu.
    document.addEventListener('pointerlockchange', () => {
      if (document.pointerLockElement && !this.renderer.xr.isPresenting && this.wristMenu.open)
        this.wristMenu.setOpen(false);
    });

    window.addEventListener('resize', () => {
      this.rig.camera.aspect = window.innerWidth / window.innerHeight;
      this.rig.camera.updateProjectionMatrix();
      this.renderer.setSize(window.innerWidth, window.innerHeight);
    });
    this.net.onRooms((rooms) => {
      this.rooms = rooms;
    });
    // A dropped connection is said out loud: the room keeps rendering, presence and queries
    // resume when the socket is back (the server replays the room on rejoin).
    this.net.socket.onStatus((connected) => {
      this.connectionLost = !connected;
      this.roomDebug()?.dashboards?.showNotice(
        connected
          ? ['Reconnected', 'Back in the room; shared state refreshed.']
          : ['Connection lost', 'Reconnecting… others cannot see you until it is back.'],
        connected ? 3000 : 8000,
      );
      if (!connected) this.audio.click();
      else this.audio.chime();
    });
  }

  /* ---------- Boot ---------- */

  async load(): Promise<void> {
    // Canvas panels draw with the shipped faces; never let the first draw fall back.
    const fonts = loadFonts();
    this.manifest = await getJson<Manifest>(api('studies'));
    await fonts;
    this.rooms = await getJson<RoomSummary[]>(api('rooms'));
    this.net.connect();
  }

  private context(): AppContext {
    if (!this.manifest) throw new Error('App not loaded');
    // eslint-disable-next-line @typescript-eslint/no-this-alias -- the context getter reads the live list
    const app = this;
    return {
      renderer: this.renderer,
      scene: this.scene,
      rig: this.rig,
      input: this.input,
      locomotion: this.locomotion,
      interactive: this.interactive,
      vignette: this.vignette,
      audio: this.audio,
      net: this.net,
      voice: this.voice,
      remoteShares: () => this.remoteShares,
      avatars: this.avatars,
      profile: this.profile,
      manifest: this.manifest,
      // A getter, not the array at this moment: scenes live for minutes and the list is
      // replaced on every rooms update (a captured array left the room board at 0 people).
      get rooms() {
        return app.rooms;
      },
      inXr: () => this.renderer.xr.isPresenting,
      remoteHeads: () => this.remotes.headPositions(),
      enterRoom: (study, code) =>
        void this.switchTo(() => new RoomScene(this.context(), study, code)),
      returnToLobby: () => void this.switchTo(() => new LobbyScene(this.context())),
      saveProfile: () => saveProfile(this.profile),
    };
  }

  /** Starts the frame loop and enters the lobby. Called once from the landing card. */
  async start(mode: 'vr' | 'desktop'): Promise<void> {
    this.audio.start();
    if (mode === 'vr') {
      // Ask for the microphone in the page before VR starts: the Quest leaves VR to show
      // the prompt and the session never comes back (2026-10-06, headset). Granted or
      // denied before, there is no prompt and VR starts at once.
      if (this.audio.context && (await micMayPrompt())) await this.voice.start(this.audio.context);
      await this.enterVr();
    }
    if (this.audio.context) void this.voice.start(this.audio.context);
    if (!this.started) {
      this.started = true;
      this.vignette.setFadeImmediate(1);
      this.renderer.setAnimationLoop((time) => this.frame(time));
      await this.switchTo(() => new LobbyScene(this.context()), true);
    }
  }

  /** Whether a VR session is running now. */
  get presenting(): boolean {
    return this.renderer.xr.isPresenting;
  }

  async xrSupported(): Promise<boolean> {
    const xr = (navigator as XrNavigator).xr;
    if (!xr) return false;
    try {
      return await xr.isSessionSupported('immersive-vr');
    } catch {
      return false;
    }
  }

  async enterVr(): Promise<void> {
    const xr = (navigator as XrNavigator).xr;
    if (!xr) throw new Error('WebXR is not available');
    // Apple Vision Pro (2026-10-07): selection is gaze and pinch (transient pointers). Its
    // tracked hands never select, would take the controller slots and draw rays from the
    // wrists, so they are not asked for there.
    const visionOs = isVisionOs(navigator.userAgent, true);
    const session = await xr.requestSession('immersive-vr', {
      optionalFeatures: visionOs
        ? ['local-floor', 'bounded-floor', 'layers']
        : ['local-floor', 'bounded-floor', 'hand-tracking', 'layers'],
    });
    // Floor level where the browser offers it; otherwise eye level, which seated mode and
    // recalibration lift to a standing height.
    const floor = await session.requestReferenceSpace('local-floor').then(
      () => true,
      () => false,
    );
    this.renderer.xr.setReferenceSpaceType(floor ? 'local-floor' : 'local');
    await this.renderer.xr.setSession(session);
    const s = session as XRSession & {
      updateTargetFrameRate?: (rate: number) => Promise<void>;
      supportedFrameRates?: Float32Array;
    };
    if (s.updateTargetFrameRate && s.supportedFrameRates?.includes(72))
      s.updateTargetFrameRate(72).catch(() => {});
    this.renderer.xr.setFoveation(0.5);
    session.addEventListener('end', () => this.hooks.onSessionEnd?.());
  }

  /* ---------- Scenes ---------- */

  private async switchTo(factory: () => SceneController, first = false): Promise<void> {
    // A request during a switch (entering a study while the lobby is still fading in) is
    // kept and run when the switch ends, never dropped: the lobby has already committed to
    // it and would otherwise wait forever.
    if (this.switching) {
      this.pendingSwitch = factory;
      return;
    }
    this.switching = true;
    // Never left half way (2026-10-07, the user: after leaving a room the screen stayed black and
    // nothing worked): a step that throws is logged, the rest still runs, and the switch always
    // ends, faded back in with walking and keys restored.
    const step = (what: string, run: () => void): void => {
      try {
        run();
      } catch (err) {
        console.error(`[clinspace] ${what} failed:`, err);
      }
    };
    try {
      if (!first) await this.vignette.fadeTo(1, 0.5);
      const old = this.current;
      if (old) {
        step(`leaving ${old.name}`, () => old.exit());
        step('clearing the scene', () => {
          this.scene.remove(old.group);
          this.interactive.clear();
          this.remotes.clear();
          this.voice.removeAll();
          this.remoteShares.clear();
          if (this.handRaised) this.toggleHand(false);
          this.wristMenu.setOpen(false);
        });
        step('disposing the old scene', () => disposeGroup(old.group));
        this.locomotion.enabled = true;
        this.input.captureText = false;
      }
      const next = factory();
      this.scene.add(next.group);
      this.current = next;
      try {
        await next.enter();
      } catch (err) {
        console.error(`[clinspace] entering ${next.name} failed:`, err);
      }
      step('the wrist menu', () =>
        this.wristMenu.setAnchors(next.anchors, next.canLeave, next.label),
      );
      step('compiling', () => this.renderer.compile(this.scene, this.rig.camera));
    } finally {
      await this.vignette.fadeTo(0, first ? 1.2 : 0.5);
      this.switching = false;
    }
    const pending = this.pendingSwitch;
    this.pendingSwitch = undefined;
    if (pending) void this.switchTo(pending);
    console.log(
      `[clinspace] scene ${this.current?.name}: ${this.renderer.info.render.triangles} tris, ${this.renderer.info.render.calls} draw calls`,
    );
  }

  /** The lobby scene when it is current (dev debug hooks). */
  lobbyDebug(): LobbyScene | undefined {
    return this.current instanceof LobbyScene ? this.current : undefined;
  }

  /** The study room scene when it is current (dev debug hooks). */
  roomDebug(): RoomScene | undefined {
    return this.current instanceof RoomScene ? this.current : undefined;
  }

  toggleMute(): boolean {
    const muted = !this.voice.isMuted;
    this.voice.setMuted(muted);
    this.net.setMuted(muted);
    return muted;
  }

  /** Sends a reaction that floats over the avatar's head for everyone else. */
  react(reaction: Reaction): void {
    if (!this.net.currentRoom) return;
    this.net.sendReaction(reaction);
    this.roomDebug()?.dashboards?.showNotice([`You ${REACTION_EMOJI[reaction]}`], 1200);
    this.audio.click();
  }

  /**
   * Saves what the user sees as a JPEG room asset (two-hand frame gesture, Z on desktop).
   * In VR the frame is rendered once more to the canvas, since the XR layer is not readable.
   */
  async takeScreenshot(): Promise<{ file: string; study: string } | undefined> {
    const room = this.roomDebug();
    if (!room) return undefined;
    // Render once more right before reading: the canvas is not preserved after a frame is
    // composited, and in VR the frames go to the XR layer, not the canvas.
    const wasXr = this.renderer.xr.enabled && this.renderer.xr.isPresenting;
    if (wasXr) this.renderer.xr.enabled = false;
    this.renderer.render(this.scene, this.rig.camera);
    const image = this.canvas.toDataURL('image/jpeg', 0.85);
    if (wasXr) this.renderer.xr.enabled = true;
    try {
      const asset = await sendJson<{ file: string; study: string }>(
        'POST',
        api('rooms', room.code, 'snapshots'),
        { kind: 'screenshot', image, by: this.profile.name },
      );
      this.lastScreenshot = asset;
      room.dashboards?.showNotice(
        ['Screenshot saved', asset.file, 'On the query log page, Room snapshots tab'],
        3500,
      );
      this.audio.chime();
      return asset;
    } catch (err) {
      room.dashboards?.showNotice(['Screenshot failed', (err as Error).message], 3000);
      return undefined;
    }
  }

  /** Routes a recognised gesture to what it does; also used by the dev hooks. */
  handleGesture(g: Gesture): void {
    this.lastGesture = g;
    const room = this.roomDebug()?.dashboards;
    switch (g.kind) {
      case 'thumbsUp':
        this.react('thumbsUp');
        break;
      case 'handRaised':
        this.toggleHand();
        break;
      case 'doublePinch':
        this.wristMenu.toggle();
        break;
      case 'pinchPull':
        room?.manipulator.holdWithHand(g.hand, true);
        break;
      case 'frame':
        void this.takeScreenshot();
        break;
    }
  }

  /** Samples the tracked hands and runs the gesture detector (XR only). */
  private updateGestures(now: number): void {
    const room = this.roomDebug()?.dashboards;
    const hands: GestureHand[] = [];
    for (let i = 0; i < this.input.slotCount; i++) {
      const h = this.input.handPose(i);
      if (!h || h.kind !== 'hand') continue;
      const pointer = this.input.pointers.find((p) => p.id === h.pointerId);
      hands.push({
        id: h.pointerId,
        curl: h.curl,
        pinch: h.pinch,
        wrist: { x: h.position.x, y: h.position.y, z: h.position.z },
        thumbTip: h.thumbTip ? { x: h.thumbTip.x, y: h.thumbTip.y, z: h.thumbTip.z } : undefined,
        hitting: pointer?.hitDistance !== undefined,
        onPanel: !!pointer && !!room?.manipulator.panelUnder(pointer),
      });
    }
    if (hands.length === 0) return;
    const head = this.rig.headPosition(new THREE.Vector3());
    for (const g of this.gestures.update(hands, { position: head }, now)) this.handleGesture(g);
    for (const h of hands)
      if (!this.gestures.holding(h.id)) room?.manipulator.holdWithHand(h.id, false);
  }

  /** Raise or lower the hand: shown on the HUD and over the avatar for everyone. */
  toggleHand(on = !this.handRaised): boolean {
    if (on === this.handRaised) return on;
    this.handRaised = on;
    this.net.setHandRaised(on);
    this.debugHud.setHandRaised(on);
    return on;
  }

  /** Pose broadcast, remote avatars, voice positions, wrist menu and HUD. */
  private updatePresence(dt: number, inXr: boolean): void {
    this.localPose.smoothMoving = this.locomotion.isMoving;
    this.localPose.speaking = this.voice.speaking;
    this.poseClock += dt;
    if (this.poseClock >= 1 / POSE_RATE_HZ) {
      this.poseClock = 0;
      if (this.net.currentRoom) this.net.sendPose(this.localPose.sample(inXr));
    }
    this.remotes.update(dt);
    this.voice.update(this.rig.camera, this.remotes.headPositions());
    if (inXr) this.updateGestures(performance.now());

    // Wrist menu: left X button edge in VR; Tab on desktop (see constructor).
    if (inXr) {
      const left = this.input.activePointers().find((p) => p.handedness === 'left');
      const down = !!left?.buttons.x;
      if (down && !this.menuButtonWasDown) this.wristMenu.toggle();
      this.menuButtonWasDown = down;
    }
    this.wristMenu.place(this.input.leftWrist(), this.rig.camera, inXr);
    // No left controller, so no X button (Vision Pro's gaze and pinch, Quest hand tracking):
    // the Menu orb opens the menu instead.
    const menuButton = this.input
      .activePointers()
      .some((p) => p.gamepad && !p.transient && p.handedness === 'left');
    this.wristMenu.placeOrb(this.rig.camera, inXr && !menuButton);
    if (this.wristMenu.open) {
      this.wristMenu.setStatus(
        this.connectionLost
          ? 'Connection lost, reconnecting...'
          : this.net.roomCode
            ? `${this.net.members.length} in the room | ${this.voice.peerCount} voice peers${this.voice.micError ? ' | mic unavailable' : ''}`
            : // The lobby is private: nobody else is ever there with you.
              this.net.currentRoom
              ? 'Private lobby | meet your team with a room code'
              : 'Connecting',
      );
    }
    this.debugHud.update({
      ...this.lastStats,
      members: this.net.members.length,
      peers: this.voice.peerCount,
      scene: this.currentSceneName,
      extra: `${this.currentSceneName} | ${this.remotes.count} avatars`,
    });
  }

  get currentSceneName(): string {
    return this.current?.name ?? 'none';
  }

  /* ---------- Frame ---------- */

  private frame(time: number): void {
    this.timer.update(time);
    const dt = Math.min(this.timer.getDelta(), 0.1);
    const inXr = this.renderer.xr.isPresenting;
    this.input.update();
    this.interactive.update();
    this.locomotion.update(dt, inXr);
    this.current?.update(dt, time);
    this.updatePresence(dt, inXr);
    updateUi();
    this.vignette.update(dt);
    this.renderer.render(this.scene, this.rig.camera);

    this.frames++;
    this.accumulator += dt;
    if (this.accumulator >= 0.5) {
      const fps = this.frames / this.accumulator;
      const info = this.renderer.info.render;
      this.lastStats = {
        fps,
        frameMs: 1000 / Math.max(1, fps),
        triangles: info.triangles,
        drawCalls: info.calls,
      };
      this.hooks.onStats?.({ ...this.lastStats, scene: this.currentSceneName });
      this.frames = 0;
      this.accumulator = 0;
    }
    if (performance.now() - this.lastLog > 10_000) {
      this.lastLog = performance.now();
      const info = this.renderer.info.render;
      console.log(
        `[clinspace] frame ${(dt * 1000).toFixed(1)} ms, ${info.triangles} tris, ${info.calls} draw calls, xr=${inXr}, scene=${this.currentSceneName}`,
      );
    }
  }
}

function disposeGroup(root: THREE.Object3D): void {
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (mesh.isMesh) {
      mesh.geometry?.dispose();
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const m of materials) {
        const mat = m as THREE.MeshStandardMaterial;
        mat.map?.dispose();
        mat.dispose();
      }
    }
  });
}
